<template>
  <div class="signup-container">
    <h1 class="page-title">서비스 필수정보</h1>
    <div class="signup-box">
      <input
        v-model="name"
        type="text"
        placeholder="이름"
        @input="validateName"
        ref="nameInput"
      />
      <p v-if="submitted && !name" class="validation-message">
        이름을 입력해주세요.
      </p>
      <p v-else-if="nameError" class="validation-message">{{ nameError }}</p>
      <select v-model="gender" @change="validateGender">
        <option disabled value="">성별</option>
        <option value="남성">남성</option>
        <option value="여성">여성</option>
      </select>
      <p v-if="submitted && !gender" class="validation-message">
        성별을 선택해주세요.
      </p>
      <input
        v-model="birthDate"
        type="text"
        placeholder="생년월일(YYYYMMDD)"
        @input="validateBirthDate"
        inputmode="numeric"
        maxlength="8"
      />
      <p v-if="birthDateError" class="validation-message">
        {{ birthDateError }}
      </p>
      <div class="calendar-options">
        <label>
          <input type="radio" value="solar" v-model="calendarType" />
          양력
        </label>
        <label>
          <input type="radio" value="lunar" v-model="calendarType" />
          음력
        </label>
      </div>
      <select v-model="birthTime">
        <option disabled value="">태어난 시간</option>
        <option v-for="hour in timeOptions" :key="hour" :value="hour">
          {{ hour }}
        </option>
      </select>
      <p v-if="submitted && !birthTime" class="validation-message">
        태어난 시간을 선택해주세요.
      </p>
      <div class="body-type-wrapper">
        <div class="body-type-title-wrapper">
          <span class="placeholder-label">체질분석</span>
          <BodyTypes
            v-if="!bodyTypeResult"
            :showQuestions="true"
            @body-type-calculated="bodyTypeResult = $event"
          />

          <input
            v-else
            type="text"
            :value="bodyTypeResult"
            readonly
            class="body-type-input"
          />
        </div>
      </div>
      <p v-if="submitted && !bodyTypeResult" class="validation-message">
        체질분석을 진행해주세요.
      </p>
      <!-- 이메일 입력 필드는 그대로 유지하거나, 필요없다면 제거 -->
      <input
        v-model="email"
        type="text"
        placeholder="이메일 (선택 사항)"
        @input="validateEmail"
        inputmode="email"
      />
      <p v-if="emailError" class="validation-message">{{ emailError }}</p>

      <button @click="saveUserData" :disabled="loading">정보 저장</button>

      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      <p class="recover-text">
        이미 입력하신 정보인가요?
        <router-link to="/RecoverUserData">정보복구</router-link>
      </p>
      <!-- successMessage는 팝업으로 대체 -->
    </div>

    <!-- 복구 코드를 보여줄 팝업 -->
    <div v-if="showRecoveryCodeModal" class="modal-overlay">
      <div class="modal-content">
        <h2>정보 저장 완료</h2>
        <p>
          정보가 성공적으로 저장되었습니다.
          <br />
          ({{ welcomeLydia }} LYD가 지급되었습니다.)
        </p>
        <p>
          아래 복구 코드를 반드시 기록해두세요.
          <br />
          계정 복구 시 필요합니다.
        </p>
        <p class="recovery-code">{{ recoveryCode }}</p>
        <button @click="closeRecoveryCodeModal">확인</button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import BodyTypes from '@/components/BodyTypes.vue'
  import { ref, inject } from 'vue'
  import { useRouter } from 'vue-router'
  import { useStore } from 'vuex'
  import { db } from '@/firebase' // Firestore 인스턴스 import
  import { doc, setDoc } from 'firebase/firestore'
  import { lunarToSolar } from 'solarlunar'
  import { GraphQLClient, gql } from 'graphql-request'
  import { generateCode } from '@/utils/utils.js'
  import { rewardUser } from '@/utils/setReward.js'

  const welcomeLydia = Number(import.meta.env.VITE_WELCOME_LYDIA)
  const apiEndpoint = import.meta.env.VITE_API_ENDPOINT
  const client = new GraphQLClient(apiEndpoint)
  const store = useStore()

  const showToast = inject('showToast')

  const name = ref('')
  const gender = ref('')
  const birthDate = ref('')
  const calendarType = ref('solar')
  const birthTime = ref('')
  const email = ref('')
  const errorMessage = ref('')
  // const successMessage = ref('') // 팝업으로 대체
  const loading = ref(false)
  const submitted = ref(false)
  const bodyTypeResult = ref('')
  const recoveryCode = ref(null)
  const showRecoveryCodeModal = ref(false) // 팝업 표시 상태

  const nameError = ref('')
  const birthDateError = ref('')
  const emailError = ref('')

  const router = useRouter()

  const timeOptions = Array.from(
    { length: 24 },
    (_, i) =>
      `${String(i).padStart(2, '0')}~${String((i + 1) % 24).padStart(2, '0')}`,
  )

  // 유효성 검사 함수들 (기존 로직 유지)
  const validateName = () => {
    nameError.value = ''
    const nameRegex = /^[가-힣a-zA-Z]+$/
    if (!name.value) {
      return
    }
    if (!nameRegex.test(name.value)) {
      nameError.value = '한글과 알파벳만 입력 가능합니다.'
    }
  }

  const validateGender = () => {
    if (gender.value == '남성' || gender.value == '여성') {
      return
    }
  }

  const validateBirthDate = () => {
    birthDateError.value = ''
    const date = birthDate.value.replace(/\D/g, '')
    if (date.length === 8) {
      const year = parseInt(date.substring(0, 4), 10)
      const month = parseInt(date.substring(4, 6), 10)
      const day = parseInt(date.substring(6, 8), 10)
      const currentYear = new Date().getFullYear()
      if (
        year < 1945 ||
        year > currentYear ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31
      ) {
        birthDateError.value = '올바른 생년월일을 입력해 주세요.'
      }
    }
  }

  const validateEmail = () => {
    emailError.value = ''
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (email.value && !emailRegex.test(email.value)) {
      emailError.value = '올바른 이메일 형식이 아닙니다.'
    }
  }

  // analyzeGanji API 호출 쿼리 정의 및 함수 (기존 로직 유지)
  const ANALYZE_GANJI_ELEMENTS_QUERY = gql`
    query AnalyzeGanjiElements($input: AnalyzeGanjiInput!) {
      analyzeGanjiElements(input: $input) {
        yearGanji
        monthGanji
        dayGanji
        hourGanji
        elementCounts {
          wood
          fire
          earth
          metal
          water
        }
      }
    }
  `

  const analyzeGanjiElements = async (
    birth,
    hourRange,
    isLunar,
    isLeapMonth = false,
  ) => {
    const variables = {
      input: {
        birth,
        hourRange,
        isLunar,
        isLeapMonth,
      },
    }
    try {
      const data = await client.request(ANALYZE_GANJI_ELEMENTS_QUERY, variables)
      return data.analyzeGanjiElements
    } catch (error) {
      console.error('Error calling analyzeGanjiElements API:', error)
      throw new Error('간지 분석 중 오류가 발생했습니다.')
    }
  }

  // 사용자 데이터 저장 함수 (팝업 로직 추가)
  const saveUserData = async () => {
    submitted.value = true
    errorMessage.value = ''
    recoveryCode.value = null

    validateName()
    validateGender()
    validateBirthDate()
    validateEmail()

    if (
      !name.value ||
      nameError.value ||
      !gender.value ||
      !birthDate.value ||
      birthDateError.value ||
      !birthTime.value ||
      !bodyTypeResult.value
    ) {
      return
    }

    loading.value = true

    const birth = birthDate.value
    const hourRange = birthTime.value
    const isLunar = calendarType.value === 'lunar'
    const isLeapMonth = false

    try {
      const data = await analyzeGanjiElements(
        birth,
        hourRange,
        isLunar,
        isLeapMonth,
      )

      const ganji = {
        yearGanji: data.yearGanji,
        monthGanji: data.monthGanji,
        dayGanji: data.dayGanji,
        hourGanji: data.hourGanji,
      }

      // accessToken은 모든 사용자에게 첫 방문 시 IntroIChingDaily에서 발급된다.
      //const accessToken = generateCode(30);
      const accessToken = localStorage.getItem('accessToken')
      const lydia = store.getters.getLydia + welcomeLydia
      const recoveryCodeValue =
        store.getters.getUser.recoveryCode || generateCode(10)

      // 신규사용자인 경우 IntroIChingDaily에서 referrer가 기록되고 추천인에게 보상이 지급된다.
      //const referrer = localStorage.getItem('referrer') || ''
      await setDoc(doc(db, 'users', accessToken), {
        name: name.value,
        gender: gender.value,
        birthDate: birthDate.value,
        calendarType: calendarType.value,
        birthTime: birthTime.value,
        email: email.value,
        ganji,
        elementCounts: data.elementCounts,
        bodyType: bodyTypeResult.value,
        lydia,
        recoveryCode: recoveryCodeValue,
        //referrer,
        createdAt: new Date().toISOString(),
      })

      // IntroIChingDaily 페이지로 옮겼다.
      /*
      localStorage.setItem('accessToken', accessToken);
      // 추천인 보상 지급 (referrer가 존재할 때만)
      if (referrer) {
        await rewardUser(store, 'refJoin', {
          referredUser: accessToken,
          referrer
        }, showToast);
      }
      */

      // Vuex store에 사용자 정보 업데이트 (initializeAuth 액션 호출 또는 직접 commit)
      // initializeAuth 액션을 호출하여 전체 로딩 및 설정 프로세스를 따르는 것이 일관적입니다.
      await store.dispatch('initializeAuth')

      // 복구 코드 설정 및 팝업 표시
      recoveryCode.value = recoveryCodeValue
      showRecoveryCodeModal.value = true // 팝업 열기
    } catch (error) {
      console.error('데이터 저장 실패:', error)
      errorMessage.value = '정보 저장에 실패했습니다. 다시 시도해주세요.'
    } finally {
      loading.value = false
    }
  }

  // 팝업 닫기 및 화면 전환 함수
  const closeRecoveryCodeModal = () => {
    showRecoveryCodeModal.value = false // 팝업 닫기
    router.replace('/IChingDaily') // 메인 화면으로 이동
  }
</script>

<style scoped>
  /* 기존 스타일 유지 */
  .signup-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    min-height: 100vh;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    padding: 1.2rem;
    background-color: #f5f5f5;
  }

  .page-title {
    font-size: var(--font-size-14);
    margin: 0.5rem 0 0.5rem 0; /* 위/아래 여백을 0.5rem로 모두 축소 */
    font-weight: bold;
    color: #333;
  }

  .signup-box {
    display: flex;
    position: relative;
    padding: 1rem;
    background: white;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    border-radius: 12px;
  }

  .signup-box input,
  .signup-box select {
    padding: 0.8rem;
    background-color: white;
    color: black;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: var(--font-size-09);
  }

  .placeholder-label {
    margin-bottom: 0.25rem;
    color: #555;
    font-size: var(--font-size-09);
  }

  .calendar-options {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    font-size: var(--font-size-09);
    margin-top: -0.5rem;
    margin-bottom: 1rem;
  }

  .validation-message {
    color: crimson;
    font-size: var(--font-size-09);
    margin-top: -0.5rem;
    margin-bottom: 0.5rem;
  }

  .body-type-title-wrapper {
    flex-direction: column;
    width: 100%;
    display: flex;
  }

  .signup-box input[readonly] {
    background-color: #eee;
  }

  .signup-box button {
    padding: 0.6rem 1.4rem;
    font-size: var(--font-size-10);
    border: none;
    border-radius: 8px;
    background-color: #333;
    color: white;
    cursor: pointer;
    &:hover {
      background-color: #555;
    }
    &:disabled {
      background-color: #888;
      cursor: not-allowed;
    }
  }

  .body-type-input {
    background-color: white;
    color: black;
  }

  .body-type-wrapper {
    width: 100%;
  }

  .recover-text,
  .error-message {
    font-size: var(--font-size-095);
    text-align: center;
  }

  /* 모달(팝업) 스타일 */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000; /* 다른 요소 위에 표시 */
  }

  .modal-content {
    background-color: white;
    padding: 1rem;
    border-radius: 12px;
    text-align: center;
    width: 90%;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  }

  .modal-content h2 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: var(--font-size-12);
    color: #333;
  }

  .modal-content p {
    margin-bottom: 1rem;
    font-size: var(--font-size-10);
    color: #555;
  }

  .modal-content .recovery-code {
    font-size: var(--font-size-11);
    font-weight: bold;
    color: #007bff; /* 강조 색상 */
    word-break: break-all;
    margin-bottom: 1.5rem;
    padding: 0.8rem;
    background-color: #e9ecef; /* 배경색 추가 */
    border-radius: 4px;
  }

  .modal-content button {
    background-color: #4caf50;
    color: white;
    padding: 0.7rem 1.5rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: var(--font-size-10);
    transition: background-color 0.3s ease;
  }

  .modal-content button:hover {
    background-color: #45a049;
  }
</style>
